<script setup lang="ts">
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { useOrgStore } from '@/stores/org'
import { useUserStore } from '@/stores/user'
import { useNotificationsStore } from '@/stores/notifications'
import { useRouter } from 'vue-router'
import { API, HOST } from '@/utils/http'
import type { BridgeLink } from '@/types/bridge'

const orgStore = useOrgStore()

const userStore = useUserStore()

const notificationsStore = useNotificationsStore()
const router = useRouter()
const currentUser = computed(() => userStore.member)
// State
const hasChecked = ref(false)
const retryTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null)

// Cleanup timeout on unmount
onBeforeUnmount(() => {
  if (retryTimeoutId.value) {
    clearTimeout(retryTimeoutId.value)
  }
})

const openBridgePage = () => {
  const orgId = orgStore.selectedOrg ?? orgStore.orgs?.[0]?.organization_id
  if (orgId) {
    router.push({ name: 'OrgPayments', params: { org_id: orgId }, query: { mode: 'kyc' } })
  } else {
    console.warn('KYCPromptDialog: no org loaded, cannot navigate to payments')
  }
}

const checkPendingKYC = async (retryCount = 0) => {
  if (hasChecked.value) return
  if (!currentUser.value?.member_id) return

  const MAX_RETRIES = 3
  const RETRY_DELAY_MS = 1000

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/payments/bridge/kyc-links/pending`
    })

    hasChecked.value = true

    // If pending link exists, create a notification that links to the Bridge page
    if (response.data.has_pending && response.data.link) {
      const link = response.data.link as BridgeLink
      const tosCompleted = link.tos_status === 'approved'
      const kycCompleted = link.kyc_status === 'approved'

      // Only show notification if not fully completed
      if (!tosCompleted || !kycCompleted) {
        notificationsStore.addNotification({
          id: 'kyc_pending',
          type: 'kyc_pending',
          title: 'Complete verification',
          description: 'Action required to enable payments',
          action: {
            label: 'Verify',
            handler: openBridgePage
          }
        })
      }
    }
  } catch (err: any) {
    // Retry on auth errors (401) - auth may not be fully settled
    if (err.response?.status === 401 && retryCount < MAX_RETRIES) {
      retryTimeoutId.value = setTimeout(() => checkPendingKYC(retryCount + 1), RETRY_DELAY_MS)
      return
    }
    // Silently fail - user may not have a Bridge account
    hasChecked.value = true
  }
}

// Watch for user login and check for pending KYC
watch(
  () => currentUser.value?.member_id,
  async (memberId, prevMemberId) => {
    if (memberId !== prevMemberId) {
      hasChecked.value = false
    }
    if (memberId && !hasChecked.value) {
      // Wait for Vue reactivity to settle, then check
      await nextTick()
      checkPendingKYC()
    }
  },
  { immediate: true }
)
</script>

<template>
  <!-- This component no longer renders a dialog, it just creates notifications -->
</template>
